import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, FolderOpen } from 'lucide-react';

function CategoryRow({ cat, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-between py-3 px-1 border-b last:border-0" data-testid={`category-row-${cat.id}`}>
      <div className="flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">{cat.name}</span>
        <Badge variant="secondary" className="text-xs">{cat.form_count || 0} forms</Badge>
      </div>
      <div className="flex gap-1">
        <Button variant="ghost" size="sm" onClick={() => onEdit(cat)} data-testid={`edit-cat-${cat.id}`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(cat)} data-testid={`delete-cat-${cat.id}`}>
          <Trash2 className="h-3.5 w-3.5 text-destructive" />
        </Button>
      </div>
    </div>
  );
}

export default function ZohoCategoryManager({ open, onOpenChange, categories, onCategoriesChanged }) {
  const [name, setName] = useState('');
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delCat, setDelCat] = useState(null);

  const reset = () => { setName(''); setEditId(null); };

  const handleSave = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await zohoAPI.updateCategory(editId, { name: name.trim() });
        toast.success('Category updated');
      } else {
        await zohoAPI.createCategory({ name: name.trim() });
        toast.success('Category created');
      }
      reset();
      onCategoriesChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save category');
    }
    setSaving(false);
  };

  const handleDelete = async () => {
    if (!delCat) return;
    try {
      await zohoAPI.deleteCategory(delCat.id);
      toast.success('Category deleted');
      setDelCat(null);
      onCategoriesChanged();
    } catch (e) {
      toast.error('Failed to delete category');
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setName(cat.name);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(val) => { if (!val) reset(); onOpenChange(val); }}>
        <DialogContent className="sm:max-w-md" data-testid="category-manager-modal">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>Organize your forms into categories like "Hiring", "Appraisals", etc.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Add/Edit form */}
            <div className="flex gap-2">
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="Category name"
                onKeyDown={e => e.key === 'Enter' && handleSave()}
                data-testid="category-name-input"
              />
              <Button onClick={handleSave} disabled={saving || !name.trim()} data-testid="save-category-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? 'Update' : <><Plus className="h-4 w-4 mr-1" />Add</>}
              </Button>
              {editId && (
                <Button variant="ghost" size="sm" onClick={reset}>Cancel</Button>
              )}
            </div>

            {/* List */}
            <div className="border rounded-md max-h-64 overflow-auto">
              {categories.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-8">No categories yet</p>
              ) : (
                categories.map(c => (
                  <CategoryRow key={c.id} cat={c} onEdit={startEdit} onDelete={setDelCat} />
                ))
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delCat} onOpenChange={(val) => !val && setDelCat(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{delCat?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Forms in this category will become uncategorized. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-cat">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
